import type { Subscription } from 'centrifuge';
import { CENTRIFUGO_CHANNELS } from '@sunflower-games/predictions-realtime-contracts';
import type { PlaeeClient } from './plaee-client';

export type ChannelMessageHandler = (raw: unknown) => void;
export type Unsubscribe = () => void;

interface ChannelEntry {
  sub: Subscription;
  handlers: Set<ChannelMessageHandler>;
  refCount: number;
  graceTimer: ReturnType<typeof setTimeout> | null;
}

export interface RealtimeManagerOptions {
  client: PlaeeClient;
  /** Delay before tearing down a channel after its last subscriber leaves. */
  graceMs?: number;
  /** Soft cap; oldest cold entries get evicted when reached. */
  maxConcurrentSubscriptions?: number;
}

const DEFAULT_GRACE_MS = 30_000;
const DEFAULT_MAX_SUBS = 100;

/**
 * Ref-counted Centrifugo subscription manager. Multiple consumers may subscribe
 * to the same channel; only the first triggers a real Centrifuge subscription,
 * only the last to leave triggers an unsubscribe — after a grace window so
 * route changes don't churn upstream.
 *
 * Mirrors the model the backend gateway used before public channels moved to
 * FE-direct, just on the FE side.
 */
export class RealtimeManager {
  private readonly client: PlaeeClient;
  private readonly graceMs: number;
  private readonly maxSubs: number;
  private readonly entries = new Map<string, ChannelEntry>();
  /** Insertion order = LRU eviction order; refreshed on subscribe + publication. */
  private readonly recencyKeys = new Set<string>();

  constructor(opts: RealtimeManagerOptions) {
    this.client = opts.client;
    this.graceMs = opts.graceMs ?? DEFAULT_GRACE_MS;
    this.maxSubs = opts.maxConcurrentSubscriptions ?? DEFAULT_MAX_SUBS;
  }

  /** Subscribe to a Centrifugo channel by name. Returns an unsubscribe fn. */
  subscribeChannel(channel: string, handler: ChannelMessageHandler): Unsubscribe {
    let entry = this.entries.get(channel);
    if (!entry) {
      this.evictIfFull();
      entry = this.openChannel(channel);
    }
    if (entry.graceTimer) {
      clearTimeout(entry.graceTimer);
      entry.graceTimer = null;
    }
    entry.handlers.add(handler);
    entry.refCount++;
    this.touchRecency(channel);
    return () => this.release(channel, handler);
  }

  /** Subscribe to a market channel by Plaee market id. */
  subscribeMarket(plaeeMarketId: string, handler: ChannelMessageHandler): Unsubscribe {
    return this.subscribeChannel(CENTRIFUGO_CHANNELS.market(plaeeMarketId), handler);
  }

  /** Subscribe to an event channel by Plaee event key. */
  subscribeEvent(eventKey: string, handler: ChannelMessageHandler): Unsubscribe {
    return this.subscribeChannel(CENTRIFUGO_CHANNELS.event(eventKey), handler);
  }

  /** Subscribe to the global events channel (`prediction-events`). */
  subscribeGlobalEvents(handler: ChannelMessageHandler): Unsubscribe {
    return this.subscribeChannel(CENTRIFUGO_CHANNELS.GLOBAL_EVENTS, handler);
  }

  /** Subscribe to the underlying-instrument firehose (`prediction-instruments`). */
  subscribeInstruments(handler: ChannelMessageHandler): Unsubscribe {
    return this.subscribeChannel(CENTRIFUGO_CHANNELS.GLOBAL_INSTRUMENTS, handler);
  }

  /**
   * Subscribe to the per-user private channel. Caller must ensure a valid
   * token is attached to the client first.
   */
  subscribeUser(plaeeCustomerId: string, handler: ChannelMessageHandler): Unsubscribe {
    return this.subscribeChannel(CENTRIFUGO_CHANNELS.user(plaeeCustomerId), handler);
  }

  /** Snapshot of current ref-counted channels — useful for diagnostics. */
  getActiveChannels(): string[] {
    return [...this.entries.keys()];
  }

  private openChannel(channel: string): ChannelEntry {
    // Defensive: an existing sub here only happens if some other code path
    // created one without going through this manager (or after a broken
    // teardown). Clear listeners so we don't pile up on top of stale ones,
    // and we'll re-issue subscribe() below.
    const existing = this.client.getExistingSubscription(channel);
    if (existing) {
      try {
        existing.removeAllListeners();
      } catch {
        // Best-effort.
      }
    }
    const sub = existing ?? this.client.newSubscription(channel);
    const entry: ChannelEntry = {
      sub,
      handlers: new Set(),
      refCount: 0,
      graceTimer: null,
    };
    sub.on('publication', (ctx: { data: unknown }) => {
      this.touchRecency(channel);
      entry.handlers.forEach((h) => {
        try {
          h(ctx.data);
        } catch (err) {
          console.error('[realtime] handler threw for channel', channel, err);
        }
      });
    });
    sub.on('subscribing', (ctx: { code?: number; reason?: string }) => {
      console.info('[realtime] subscribing', channel, ctx.code, ctx.reason);
    });
    sub.on('subscribed', (ctx: { recoverable?: boolean; positioned?: boolean }) => {
      console.info('[realtime] subscribed', channel, ctx);
    });
    sub.on('unsubscribed', (ctx: { code?: number; reason?: string }) => {
      console.warn('[realtime] unsubscribed', channel, ctx.code, ctx.reason);
    });
    sub.on('error', (ev: { error?: { code?: number; message?: string } }) => {
      console.error('[realtime] subscription error', channel, ev.error?.code, ev.error?.message, ev);
    });
    // Always call subscribe(). Centrifuge no-ops on already-subscribed subs;
    // re-subscribes the orphan-but-registered sub returned by `existing`.
    sub.subscribe();
    this.entries.set(channel, entry);
    return entry;
  }

  private release(channel: string, handler: ChannelMessageHandler): void {
    const entry = this.entries.get(channel);
    if (!entry) return;
    if (!entry.handlers.delete(handler)) return;
    entry.refCount--;
    if (entry.refCount > 0) return;

    entry.graceTimer = setTimeout(() => {
      const stillIdle = this.entries.get(channel);
      if (!stillIdle || stillIdle.refCount > 0) return;
      this.teardownChannel(channel, stillIdle);
    }, this.graceMs);
  }

  private teardownChannel(channel: string, entry: ChannelEntry): void {
    try {
      entry.sub.unsubscribe();
      entry.sub.removeAllListeners();
    } catch {
      // Best-effort.
    }
    // Centrifuge keeps the sub in its registry across `unsubscribe()`; we must
    // explicitly remove it so the next subscribe through this channel gets a
    // fresh `Subscription` instead of reusing the now-orphan one.
    this.client.removeSubscription(entry.sub);
    this.entries.delete(channel);
    this.recencyKeys.delete(channel);
  }

  private touchRecency(channel: string): void {
    if (this.recencyKeys.has(channel)) {
      this.recencyKeys.delete(channel);
    }
    this.recencyKeys.add(channel);
  }

  private evictIfFull(): void {
    if (this.entries.size < this.maxSubs) return;
    for (const channel of this.recencyKeys) {
      const entry = this.entries.get(channel);
      if (entry && entry.refCount === 0) {
        if (entry.graceTimer) clearTimeout(entry.graceTimer);
        this.teardownChannel(channel, entry);
        return;
      }
    }
    // Every entry is hot. Drop the oldest anyway — the consumer that held it
    // will get an error and can re-subscribe.
    const oldest = this.recencyKeys.values().next().value;
    if (!oldest) return;
    const entry = this.entries.get(oldest);
    if (entry) this.teardownChannel(oldest, entry);
  }
}
